import React from 'react';

import AllocationBreakdownCard from './AllocationBreakdownCard';
import { ProjectedIssueRecommendedAction } from './ProjectedIssueRecommendedAction';
import { TransferInventoryControl } from './TransferInventoryControl';

/**
 * Detail view for a projected issue
 * Displays SKU info, available inventory, system recommendation, and allocation breakdown
 *
 * @param {Object} props
 * @param {string} props.externalSku - SKU identifier
 * @param {string} props.skuName - Product name
 * @param {string} props.issueChannel - Channel with issue
 * @param {string} props.issueType - Type of issue
 * @param {number} props.quantity - Quantity of issue
 * @param {number} props.totalAvailableUnits - Total available units
 * @param {string} props.recommendedAction - Recommended action
 * @param {Object} props.wholesaleAllocationBreakdown - Wholesale allocation data
 * @param {Object} props.firstPartyAllocationBreakdown - First party allocation data
 * @param {Function} props.onApplyRecommendedAction - Callback when Apply Recommended Action button is clicked
 * @param {Function} props.onSaveTransfer - Callback when Save Transfer button is clicked
 */
export function ProjectedIssueDetail({
    externalSku,
    firstPartyAllocationBreakdown = {},
    issueChannel,
    issueType,
    onApplyRecommendedAction,
    onSaveTransfer,
    quantity,
    recommendedAction,
    skuName,
    totalAvailableUnits,
    wholesaleAllocationBreakdown = {},
}) {
    if (!externalSku) {
        return (
            <div className="flex items-center justify-center h-full text-gray-500">
                <p>Select an item to view details</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg">
            <div className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-start">
                    {/* Left: SKU and Product Name */}
                    <div>
                        <div className="text-lg font-bold text-gray-900">SKU-{externalSku}</div>
                        <div className="text-base text-left text-gray-600">{skuName}</div>
                    </div>

                    {/* Right: Available Inventory */}
                    <div className="text-right">
                        <div className="text-base text-gray-600">Available Inventory</div>
                        <div className="text-lg font-bold text-[#004693] tabular-nums">
                            {totalAvailableUnits.toLocaleString()}
                        </div>
                    </div>
                </div>

                {/* System Recommendation */}
                <div>
                    <ProjectedIssueRecommendedAction
                        recommendedAction={recommendedAction}
                        quantity={quantity}
                        issueType={issueType}
                        issueChannel={issueChannel}
                        onApply={onApplyRecommendedAction}
                    />
                </div>

                {/* Current Allocation Breakdown */}
                <div>
                    <h2 className="text-base text-left font-bold text-gray-900 mb-4">Current Allocation Breakdown</h2>
                    <div className="space-y-4">
                        {/* Wholesale Allocation */}
                        <AllocationBreakdownCard
                            title="Wholesale Allocation"
                            titleColor="text-[#0074a9]"
                            initialAllocation={wholesaleAllocationBreakdown.initialAllocation}
                            confirmedOrders={wholesaleAllocationBreakdown.confirmedOrders}
                            packed={wholesaleAllocationBreakdown.packedQuantity}
                            adjustments={wholesaleAllocationBreakdown.adjustments}
                            receipts={wholesaleAllocationBreakdown.receivedQuantity}
                            transfers={wholesaleAllocationBreakdown.transfers}
                            showInCartAmount={false}
                            showSoftCommittedAmount
                            remainingAvailable={wholesaleAllocationBreakdown.remainingAvailable}
                        />

                        {/* First Party Allocation */}
                        <AllocationBreakdownCard
                            title="First Party Allocation"
                            titleColor="text-[#d40824]"
                            initialAllocation={firstPartyAllocationBreakdown.initialAllocation}
                            confirmedOrders={firstPartyAllocationBreakdown.confirmedOrders}
                            packed={firstPartyAllocationBreakdown.packedQuantity}
                            adjustments={firstPartyAllocationBreakdown.adjustments}
                            receipts={firstPartyAllocationBreakdown.receivedQuantity}
                            transfers={firstPartyAllocationBreakdown.transfers}
                            inCart={firstPartyAllocationBreakdown.inCart}
                            showInCartAmount
                            showSoftCommittedAmount
                            remainingAvailable={firstPartyAllocationBreakdown.remainingAvailable}
                        />
                    </div>
                </div>

                {/* Transfer Inventory Between Channels */}
                <TransferInventoryControl
                    key={externalSku}
                    primaryLabel="Wholesale"
                    secondaryLabel="First Party"
                    initialPrimary={wholesaleAllocationBreakdown.remainingAvailable}
                    initialSecondary={firstPartyAllocationBreakdown.remainingAvailable}
                    onSaveTransfer={onSaveTransfer}
                />
            </div>
        </div>
    );
}
